import {
  ACCOUNT_TYPES,
  type LIQUIDITY_NEEDS,
} from "@tastyworks/shared-utils/misc/constants"
import { includes } from "lodash-es"
import {
  ACCOUNT_BENEFICIARY_DESER,
  AccountBeneficiary,
  type INVESTMENT_OBJECTIVES,
  RiskTolerance,
  TimeHorizon,
} from "./account"
import { ENTITY_ACCOUNT_TYPES, RETIREMENT_ACCOUNT_TYPES } from "./account-type"
import { type SchemaDeSer, SchemaDeSerBuilder } from "./deser"
import type { OPTIONS_LEVEL } from "./options-level"

// AO1
export const EMBER_AUTH_COOKIE_KEY = "ember_simple_auth-session"
// AO2
export const AO2_AUTH_SESSION_COOKIE_KEY = "ao2_auth-session"

export enum STATUSES {
  IN_PROGRESS = "In Progress",
  SUBMITTED = "Submitted",
  APPROVED = "Approved",
  SEND_FAILED = "Send Failed",
  REJECTED = "Rejected",
  NEEDS_REVIEW = "Needs Review",
  SUPPORTING_DOCUMENTS_REQUIRED = "Supporting Documents Required",
  ACTION_REQUIRED = "Action Required",
  CANCELED = "Canceled",
  COMPLETE = "Complete",
  SUPPORTING_DOCUMENTS_APPROVED = "Supporting Documents Approved",
  NEEDS_PRINCIPAL_APPROVAL = "Needs Principal Approval",
  IN_REVIEW = "In Review",
}

const REVIEW_STATUSES = [
  STATUSES.SEND_FAILED,
  STATUSES.SUPPORTING_DOCUMENTS_APPROVED,
  STATUSES.SUPPORTING_DOCUMENTS_REQUIRED,
  STATUSES.NEEDS_PRINCIPAL_APPROVAL,
  STATUSES.IN_REVIEW,
  STATUSES.REJECTED,
  STATUSES.NEEDS_REVIEW,
  STATUSES.ACTION_REQUIRED,
]

export enum ACCOUNT_ACTIVITY_TYPES {
  ACTIVE = "Active Trading",
  LONG_TERM = "Long Term Investing",
  SHORT_TERM = "Short Term Investing",
}

export enum EXPECTED_WITHDRAWAL_TYPES {
  FREQUENT = "FREQUENT",
  OCCASIONAL = "OCCASIONAL",
  RARE = "RARE",
}

export enum INITIAL_DEPOSIT_TYPES {
  ACCOUNT_TRANSFER = "Account Transfer",
  WIRE = "Wire",
  ACH = "ACH",
  CHECK = "Check",
}

export const FOREIGN_INITIAL_DEPOSIT_TYPES = [
  INITIAL_DEPOSIT_TYPES.ACCOUNT_TRANSFER,
  INITIAL_DEPOSIT_TYPES.WIRE,
]

export const ENTITY_INITIAL_DEPOSIT_TYPES = Object.values(INITIAL_DEPOSIT_TYPES)

export enum INITIAL_FUNDING_SOURCE {
  CORPORATE_INCOME = "Corporate Income",
  INHERITED_FUNDS = "Inherited Funds",
  INVESTMENT_CAPITAL = "Investment Capital",
  PERSONAL_SAVINGS = "Personal",
}

export enum REFERRAL_SOURCES {
  TASTYLIVE = "TASTYLIVE",
  INTERNET = "INTERNET_SEARCH",
  LIVE_EVENTS = "LIVE_EVENTS",
  PERSONAL_REFERRAL = "PERSONAL_REFERRAL",
}

export class ApplicantData {
  static onInitialize = (_obj: ApplicantData) => {
    /* no-op */
  }

  constructor() {
    ApplicantData.onInitialize(this)
  }

  acceptedAgreements = false
  email = ""
  // oxlint-disable-next-line @typescript-eslint/no-magic-numbers
  estatePercent = 0.5
  firstName = ""
  lastName = ""
  middleName = ""
  prefixName = ""
  suffixName = ""

  get isComplete() {
    return this.acceptedAgreements
  }

  get fullName() {
    return [
      this.prefixName,
      this.firstName,
      this.middleName,
      this.lastName,
      this.suffixName,
    ]
      .filter((n) => n !== "")
      .join(" ")
  }
}

export const APPLICANT_DATA_DESER: SchemaDeSer<ApplicantData> =
  new SchemaDeSerBuilder(ApplicantData)
    .ofBoolean("acceptedAgreements")
    .ofString("email")
    .ofFloat("estatePercent")
    .ofString("firstName")
    .ofString("lastName")
    .ofString("middleName")
    .ofString("prefixName")
    .ofString("suffixName")
    .toDeSer()

export class Applicant {
  static onInitialize = (_obj: Applicant) => {
    /* no-op */
  }

  constructor() {
    Applicant.onInitialize(this)
  }

  applicantData = new ApplicantData()

  get isComplete() {
    return this.applicantData.isComplete
  }

  get fullName() {
    return this.applicantData.fullName
  }
}

export const APPLICANT_DESER: SchemaDeSer<Applicant> = new SchemaDeSerBuilder(
  Applicant
)
  .ofNested("applicantData", APPLICANT_DATA_DESER, ApplicantData)
  .toDeSer()

export class AccountOpeningApplication {
  static onInitialize = (_obj: AccountOpeningApplication) => {
    /* no-op */
  }

  constructor() {
    AccountOpeningApplication.onInitialize(this)
  }

  accountTypeName = ACCOUNT_TYPES.INDIVIDUAL
  id = "0"
  investmentObjective: INVESTMENT_OBJECTIVES | null = null
  marginOrCash = "Margin"
  optionsLevel: OPTIONS_LEVEL | null = null
  status = STATUSES.IN_PROGRESS
  entityId: number | null | undefined = null
  externalReason = ""
  accountNumber = ""
  applicants: Applicant[] = []
  accountBeneficiaries: AccountBeneficiary[] = []
  decedentName: string | null = null
  isForeign = false

  accountActivityType: ACCOUNT_ACTIVITY_TYPES | null = null
  primaryBanking: string | null = null
  initialDepositType: INITIAL_DEPOSIT_TYPES | null = null
  initialFundingSource: INITIAL_FUNDING_SOURCE | null = null
  valueInitialDeposit: number | null = null
  expectedWithdrawals: EXPECTED_WITHDRAWAL_TYPES | null = null
  letterOfExplanation: string | null = null
  liquidityNeeds: LIQUIDITY_NEEDS | null = null
  source = ""
  initialContact: REFERRAL_SOURCES | null = null
  referredName: string | null = null
  referredRelationship: string | null = null
  isReferredToBroker: boolean | null = null
  riskTolerance: RiskTolerance | null = null
  investmentTimeHorizon: TimeHorizon | null = null
  isAutoAppealEligible = false

  get isIndividual() {
    return this.accountTypeName === ACCOUNT_TYPES.INDIVIDUAL
  }

  get isRetirement() {
    return includes(RETIREMENT_ACCOUNT_TYPES, this.accountTypeName)
  }

  get isJointContinuation() {
    return this.status === STATUSES.IN_PROGRESS
  }

  get isEntity() {
    return includes(ENTITY_ACCOUNT_TYPES, this.accountTypeName)
  }

  get isSubmitted() {
    return this.status === STATUSES.SUBMITTED
  }

  get isApproved() {
    return this.status === STATUSES.APPROVED
  }

  get isSendFailed() {
    return this.status === STATUSES.SEND_FAILED
  }

  get isRejected() {
    return this.status === STATUSES.REJECTED
  }

  get needsReview() {
    return REVIEW_STATUSES.includes(this.status)
  }

  get isActionRequired() {
    return this.needsReview || this.isJointContinuation
  }

  get isCanceled() {
    return this.status === STATUSES.CANCELED
  }

  get isComplete() {
    return this.status === STATUSES.COMPLETE
  }

  get isProcessing() {
    return this.isSubmitted || this.isApproved
  }
}

export const ACCOUNT_OPENING_APPLICATION_DESER: SchemaDeSer<AccountOpeningApplication> =
  new SchemaDeSerBuilder(AccountOpeningApplication)
    .ofString("accountTypeName")
    .ofString("id")
    .ofString("investmentObjective")
    .ofString("marginOrCash")
    .ofString("optionsLevel")
    .ofString("status")
    .ofInt("entityId")
    .ofString("externalReason")
    .ofString("accountNumber")
    .ofString("letterOfExplanation")
    .ofArray("applicants", APPLICANT_DESER, Applicant)
    .ofBoolean("isForeign")
    .ofArray(
      "accountBeneficiaries",
      ACCOUNT_BENEFICIARY_DESER,
      AccountBeneficiary
    )
    .ofString("decedentName")
    .ofString("accountActivityType")
    .ofString("primaryBanking")
    .ofString("initialContact")
    .ofString("referredName")
    .ofString("referredRelationship")
    .ofBoolean("isReferredToBroker")
    .ofString("initialDepositType")
    .ofString("initialFundingSource")
    .ofFloat("valueInitialDeposit")
    .ofString("expectedWithdrawals")
    .ofString("liquidityNeeds")
    .ofString("source")
    .ofString("riskTolerance")
    .ofString("investmentTimeHorizon")
    .ofBoolean("isAutoAppealEligible")
    .toDeSer()

export const ACCOUNT_OPENING_APPLICATION_PARSER =
  ACCOUNT_OPENING_APPLICATION_DESER.toParser(AccountOpeningApplication)
